import { useEffect, useSyncExternalStore } from "react";
import { ORDERED_MULTI_ATTACHMENT_CAPABILITY } from "@/features/post/lib/orderedMultiAttachmentContract";
import { apiGet } from "@/shared/api";

export {
  ORDERED_MULTI_ATTACHMENT_API_VERSION,
  ORDERED_MULTI_ATTACHMENT_CAPABILITY,
} from "@/features/post/lib/orderedMultiAttachmentContract";

export type OrderedMultiAttachmentCapabilityStatus =
  | "checking"
  | "available"
  | "unavailable"
  | "failed";

const STATUS_MESSAGE: Record<
  Exclude<OrderedMultiAttachmentCapabilityStatus, "available">,
  string
> = {
  checking:
    "複数添付の対応状況を確認中です。確認が完了するまでは添付は1個までです。",
  unavailable: "接続先が複数添付に対応していないため、添付は1個までです。",
  failed: "複数添付の対応確認に失敗したため、安全のため添付は1個までです。",
};

interface CapabilitiesResponse {
  capabilities: string[];
}

let cachedStatus: OrderedMultiAttachmentCapabilityStatus = "checking";
let preflight: Promise<OrderedMultiAttachmentCapabilityStatus> | null = null;
const listeners: Set<() => void> = new Set();

function getSnapshot(): OrderedMultiAttachmentCapabilityStatus {
  return cachedStatus;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

function updateStatus(status: OrderedMultiAttachmentCapabilityStatus): void {
  if (cachedStatus === status) return;
  cachedStatus = status;
  for (const listener of listeners) listener();
}

function hasOrderedMultiAttachmentCapability(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const capabilities = (value as Partial<CapabilitiesResponse>).capabilities;
  return (
    Array.isArray(capabilities) &&
    capabilities.includes(ORDERED_MULTI_ATTACHMENT_CAPABILITY)
  );
}

export function preflightOrderedMultiAttachmentCapability(): Promise<OrderedMultiAttachmentCapabilityStatus> {
  if (cachedStatus !== "checking") return Promise.resolve(cachedStatus);
  if (preflight) return preflight;

  preflight = apiGet<unknown>("/capabilities", {
    cache: "no-store",
  })
    .then((response) => {
      const status = hasOrderedMultiAttachmentCapability(response)
        ? "available"
        : "unavailable";
      updateStatus(status);
      return status;
    })
    .catch(() => {
      updateStatus("failed");
      return "failed";
    });
  return preflight;
}

export function useOrderedMultiAttachmentCapability(): OrderedMultiAttachmentCapabilityStatus {
  const status = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    void preflightOrderedMultiAttachmentCapability();
  }, []);
  return status;
}

export function orderedMultiAttachmentStatusMessage(
  status: OrderedMultiAttachmentCapabilityStatus,
): string | null {
  return status === "available" ? null : STATUS_MESSAGE[status];
}

/** mixed-version応答などで契約不一致を検出した際、次回の確認を強制する。 */
export function invalidateOrderedMultiAttachmentCapability(): void {
  preflight = null;
  updateStatus("checking");
}

export function resetOrderedMultiAttachmentCapabilityForTest(): void {
  invalidateOrderedMultiAttachmentCapability();
}
